// شناسنامه قراردادها — گزارش خواندنی جامع یک قرارداد
import React, { useState, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePager } from '../../components/paging';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtNum = n => Number(n).toLocaleString('fa-IR');

const API = (path, opts = {}) => fetch(`/api/contract${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const SECTIONS = [
  ['request', 'صورت وضعیت'], ['havaleh', 'حواله'], ['cost', 'مبلغ قرارداد'],
  ['deposit', 'سپرده'], ['refund', 'استرداد'], ['zemanat', 'ضمانتنامه'],
];

// ستون‌هایی که عددی هستند (برای راست‌چین و جداکننده هزارگان)
const NUM_COLS = new Set(['مبلغ', 'جمع', 'خالص', 'مالیات بر ارزش افزوده', 'بیمه سهم دولت', 'جمع کسورات',
  'پیش پرداخت', 'مبلغ سپرده', 'استرداد شده', 'مانده استرداد نشده', 'مبلغ استرداد', 'مبلغ ضمانتنامه',
  'مبلغ صورت وضعیت', 'سریال', 'سریال صورت وضعیت']);

function ContractLookup({ onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (text) => { setQ(text); const j = await API(`/contract-history/lookup/?q=${encodeURIComponent(text)}`); if (j.ok) { setRows(j.rows); setOpen(true); } };
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: '260px' }}>
      <input value={q} onFocus={() => search(q)} onChange={e => search(e.target.value)} placeholder="🔍 انتخاب قرارداد..."
        style={{ ...ui.searchInput, width: '100%', boxSizing: 'border-box' }} />
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 700, maxHeight: '300px', overflowY: 'auto', marginTop: '2px' }}>
          {rows.map(r => (
            <div key={r.id} onClick={() => { onPick(r); setOpen(false); setQ(r.display); }}
              style={{ padding: '9px 12px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}
              onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>{r.display}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: '10px', padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', minWidth: '130px', flex: 1 }}>
      <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: '700', color: color || '#1a2f50' }}>{value}</div>
    </div>
  );
}

export default function ContractHistoryReport({ user, onBack }) {
  const [contract, setContract] = useState(null);
  const [header, setHeader]     = useState(null);
  const [section, setSection]   = useState('request');
  const [table, setTable]       = useState({ columns: [], rows: [] });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const pager = usePager();          // صفحه‌بندی مشترک — ۲۰۰ ردیف در هر صفحه

  const loadSection = useCallback(async (cid, sec, page = 1) => {
    setLoading(true); setError('');
    const j = await API(`/contract-history/${cid}/${sec}/?page=${page}`);
    setLoading(false);
    if (!j.ok) { setError(j.msg); setTable({ columns: [], rows: [] }); pager.reset(); return; }
    setTable(prev => ({
      columns: j.columns,
      rows: pager.merge(page > 1 ? prev.rows : [], j, page),
    }));
  }, [pager]);

  const pickContract = async (r) => {
    setContract(r); setSection('request'); setError('');
    const h = await API(`/contract-history/${r.id}/header/`);
    if (h.ok) setHeader(h.header); else setHeader(null);
    loadSection(r.id, 'request');
  };

  const changeSection = (sec) => { setSection(sec); if (contract) loadSection(contract.id, sec); };

  const toolbar = (
    <>
      <ContractLookup onPick={pickContract} />
      {contract && <div style={ui.countBadge}>{header ? `${toFa(header.request_count)} صورت‌وضعیت` : '...'}</div>}
    </>
  );

  return (
    <ListPage user={user} onBack={onBack} title="شناسنامه قراردادها" subtitle="بخش قراردادها — سامانه ارجان"
      toolbar={toolbar} error={error}
      pager={pager} loading={loading} onMore={p => contract && loadSection(contract.id, section, p)}>
      {!contract ? (
        <div style={ui.empty}>برای مشاهدهٔ شناسنامه، یک قرارداد را انتخاب کنید</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* شناسنامه (هویت + خلاصه) */}
          {header && (
            <div style={{ background: '#f0f2f7', borderRadius: '12px', padding: '12px 16px', marginBottom: '10px', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '12px', color: '#333', marginBottom: '10px' }}>
                <span><b>قرارداد:</b> {header.contract_no}__{header.contract_date}</span>
                <span><b>پیمانکار:</b> {header.contractor || '—'}</span>
                <span><b>نوع:</b> {header.contract_type || '—'}</span>
                <span><b>حوزه اجرایی:</b> {header.executive_domain || '—'}</span>
                <span><b>ناظر:</b> {header.supervisor || '—'}</span>
                <span><b>موضوع:</b> {header.subject || '—'}</span>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <Stat label="مبلغ قرارداد" value={fmtNum(header.contract_cost)} color="#1a237e" />
                <Stat label="جمع صورت‌وضعیت" value={fmtNum(header.sum_request)} color="#00695c" />
                <Stat label="مانده قرارداد" value={fmtNum(header.remain)} color={header.remain < 0 ? '#b71c1c' : '#1b5e20'} />
                <Stat label="جمع حواله" value={fmtNum(header.sum_havaleh)} color="#e65100" />
                <Stat label="جمع ضمانتنامه" value={fmtNum(header.sum_zemanat)} color="#6a1b9a" />
              </div>
            </div>
          )}

          {/* تب بخش‌ها */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap', flexShrink: 0 }}>
            {SECTIONS.map(([k, t]) => (
              <button key={k} onClick={() => changeSection(k)}
                style={{ padding: '7px 16px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700',
                  background: section === k ? 'linear-gradient(135deg,#004d40,#00695c)' : '#e0e0e0', color: section === k ? '#fff' : '#555' }}>{t}</button>
            ))}
          </div>

          {/* جدول عمومی بخش */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', borderRadius: '10px', border: '1px solid #eee', background: '#fff' }}>
            {loading ? (
              <div style={ui.empty}>⏳ در حال بارگذاری...</div>
            ) : table.rows.length === 0 ? (
              <div style={ui.empty}>📭 در این بخش موردی ثبت نشده است</div>
            ) : (
              <table style={{ ...ui.table, tableLayout: 'auto' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                  <tr>{table.columns.map((c, i) => <th key={i} style={{ ...ui.th, whiteSpace: 'nowrap' }}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {table.rows.map((row, ri) => (
                    <tr key={ri} style={{ background: ri % 2 === 0 ? '#fff' : '#fdf6e3' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
                      onMouseLeave={e => e.currentTarget.style.background = ri % 2 === 0 ? '#fff' : '#fdf6e3'}>
                      {row.map((v, ci) => {
                        const col = table.columns[ci];
                        const isNum = NUM_COLS.has(col) && typeof v === 'number';
                        return (
                          <td key={ci} style={{ ...(isNum ? ui.tdNum : ui.td), textAlign: isNum ? 'center' : 'right',
                            color: isNum ? '#1b5e20' : '#111', whiteSpace: 'nowrap' }}>
                            {v === '' || v === null ? '•' : isNum ? fmtNum(v) : (typeof v === 'number' ? toFa(v) : v)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div style={{ textAlign: 'center', padding: '6px 0 2px', fontSize: '12px', color: '#888', flexShrink: 0 }}>
            {table.rows.length > 0 &&
              (table.rows.length < pager.total
                ? `${toFa(table.rows.length)} از ${toFa(pager.total)} ردیف`
                : `${toFa(table.rows.length)} ردیف`)}
          </div>
        </div>
      )}
    </ListPage>
  );
}
